import React from "react"
import PropTypes from "prop-types"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert
} from "@mui/material"

/**
 * Create / edit form for a chapter (a section of a course's curriculum).
 * `error` is the last save failure, shown inside the dialog because the
 * page's toast would open underneath it.
 */
export default function ChapterFormDialog({
    open,
    chapter,
    course,
    saving,
    error,
    onClose,
    onSubmit
}) {
    const [form, setForm] = React.useState({ title: "", description: "" })
    const [titleError, setTitleError] = React.useState("")

    React.useEffect(() => {
        if (!open) return
        setTitleError("")
        setForm({
            title: chapter?.title || "",
            description: chapter?.description || ""
        })
    }, [open, chapter])

    const handleSubmit = () => {
        if (!form.title.trim()) {
            setTitleError("Title is required")
            return
        }
        setTitleError("")
        onSubmit({
            title: form.title.trim(),
            description: form.description.trim() || null
        })
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {chapter ? "Edit chapter" : "Add a chapter"}
                {course?.title && (
                    <span className="mt-0.5 block text-xs font-normal text-slate-500">
                        {course.title}
                    </span>
                )}
            </DialogTitle>

            <DialogContent dividers>
                <div className="space-y-4 pt-1">
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        label="Chapter title"
                        placeholder="e.g. Getting started"
                        value={form.title}
                        inputProps={{ maxLength: 300 }}
                        onChange={(event) =>
                            setForm((prev) => ({ ...prev, title: event.target.value }))
                        }
                        error={Boolean(titleError)}
                        helperText={titleError}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        label="Description (optional)"
                        value={form.description}
                        onChange={(event) =>
                            setForm((prev) => ({
                                ...prev,
                                description: event.target.value
                            }))
                        }
                    />
                    {error && <Alert severity="error">{error}</Alert>}
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                    {chapter ? "Save changes" : "Add chapter"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ChapterFormDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    chapter: PropTypes.object,
    course: PropTypes.object,
    saving: PropTypes.bool,
    error: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
}
